import React, { useContext } from "react";

import classNames from "classnames";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import { useRouter } from "../../hooks/useNavigation";
import { rgba } from "polished";
import PropTypes from "prop-types";
import qs from "qs";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import getQueryString from "../../helpers/getQueryString/getQueryString";
import Markdown from "../../styles/Markdown";
import {
  horizontalAlignmentMap,
  verticalAlignmentMap,
} from "../../styles/positionMaps";

const StyledOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${({ theme }) => rgba(`${theme.primaryColor}`, 0.7)};
  z-index: 1;
`;

const StyledImageWrapper = styled.div`
  ${({ $isInline }) =>
    $isInline
      ? `
      position: relative;
      height: 100%;
    `
      : ""}

  img {
    border-radius: ${({ $borderRadius }) => $borderRadius};
  }
`;
const StyledRoleName = styled.div`
  z-index: 2;
`;

const StyledBoxLink = styled.a`
  height: ${({ dimensions }) => dimensions.height};
  width: ${({ dimensions }) => dimensions.width || "100%"};
  position: relative;
  min-height: ${({ dimensions }) => dimensions.height || "175px"};
  max-height: 400px;
  max-width: 100%;
  margin: 0 auto;
  display: ${({ $isInline }) => ($isInline ? "flex" : "block")};
  flex-direction: ${({ $isInline }) => ($isInline ? "column-reverse" : "unset")};
`;

const StyledBoxDiv = styled.div`
  height: ${({ dimensions }) => dimensions.height};
  width: ${({ dimensions }) => dimensions.width || "100%"};
  position: relative;
  min-height: ${({ dimensions }) => dimensions.height || "175px"};
  max-height: 400px;
  max-width: 100%;
  margin: 0 auto;
  display: ${({ $isInline }) => ($isInline ? "flex" : "block")};
  flex-direction: ${({ $isInline }) => ($isInline ? "column-reverse" : "unset")};
`;

const StyledContainer = styled.div`
  height: fit-content !important;
`;

function BoxLink(props) {
  const { boxLink, columnValue, boxDimensions } = props;

  const { text, query, image, hardcodedURL } = boxLink;

  const imageUrl = image?.url;
  const dimensions = {
    height: boxDimensions.height,
    width: boxDimensions.width,
  };

  const {
    url,
    objectFit = "contain",
    showOverlay,
    isTextBeneath = true,
    horizontalTextAlignment,
    verticalTextAlignment,
    borderRadius,
  } = useContext(ComponentContext);

  const router = useRouter();

  const queryObject = qs.parse(query?.slice(1));
  const queryString = getQueryString(queryObject);
  const boxHref = [url, "?", queryString].filter(Boolean).join("");

  const isQueryUsed = query?.includes("?");

  let urlToUse = isQueryUsed ? boxHref : query;

  if (hardcodedURL && hardcodedURL.length > 0) {
    urlToUse = hardcodedURL;
  }

  // Check if we have a valid URL to determine if this should be a link
  const hasValidUrl =
    urlToUse && urlToUse.trim().length > 0 && urlToUse !== "undefined";

  const handleClick = (e) => {
    if (!hasValidUrl) return;

    e.preventDefault();
    if (hardcodedURL && hardcodedURL.length > 0) {
      router.push(hardcodedURL);
    } else {
      router.push(
        {
          pathname: isQueryUsed ? url : query,
          query: queryString,
        },
        undefined,
        {
          // Always scroll to the top of the page when navigating to a new page
          scroll: true,
        }
      );
    }
  };

  const roleNameWrapperClassName = classNames(
    "p-3 d-flex flex-grow-1 w-100  text-white text-center text-uppercase",
    {
      "position-absolute": !isTextBeneath,
      "h-100": !isTextBeneath,
    },
    `justify-content-${
      horizontalAlignmentMap[horizontalTextAlignment || "center"]
    }`,
    `align-items-${verticalAlignmentMap[verticalTextAlignment || "center"]}`
  );

  const commonProps = {
    className: `position-relative boxLink justify-content-center ${
      hasValidUrl ? "text-decoration-none" : ""
    }`,
    dimensions,
    $isInline: isTextBeneath,
  };

  const content = (
    <>
      <StyledRoleName className={roleNameWrapperClassName}>
        <Markdown>{text}</Markdown>
      </StyledRoleName>
      {showOverlay && <StyledOverlay dimensions={dimensions} />}
      {imageUrl && (
        <StyledImageWrapper
          $isInline={isTextBeneath}
          $borderRadius={borderRadius}
        >
          <Image
            alt={text || image?.alternativeText}
            layout="fill"
            objectFit={objectFit}
            priority
            src={imageUrl}
          />
        </StyledImageWrapper>
      )}
    </>
  );

  return (
    <StyledContainer
      className={`mb-4 col-lg-${columnValue} col-md-4 col-sm-6 col-12 position-relative`}
      role={hasValidUrl ? "button" : undefined}
    >
      {hasValidUrl ? (
        <StyledBoxLink {...commonProps} href={urlToUse} onClick={handleClick}>
          <StyledRoleName className={roleNameWrapperClassName}>
            <Markdown>{text}</Markdown>
        </StyledRoleName>
        {showOverlay && <StyledOverlay dimensions={dimensions} />}
        {imageUrl && (
            <Image
              alt={text || image?.alternativeText}
              layout="fill"
              objectFit={objectFit}
              priority
              src={imageUrl}
            />
        )}
      </StyledBoxLink>
      ) : (
        <StyledBoxDiv {...commonProps}><StyledRoleName className={roleNameWrapperClassName}>
          <Markdown>{text}</Markdown>
        </StyledRoleName>
        {showOverlay && <StyledOverlay dimensions={dimensions} />}
        {imageUrl && (
          <StyledImageWrapper
            isInline={isTextBeneath}
            $borderRadius={borderRadius}
          >
            <Image
              alt={text || image?.alternativeText}
              layout="fill"
              objectFit={objectFit}
              priority
              src={imageUrl}
            />
          </StyledImageWrapper>
        )}</StyledBoxDiv>
      )}
    </StyledContainer>
  );
}

export default observer(BoxLink);

BoxLink.defaultProps = {
  boxDimensions: null,
};

BoxLink.propTypes = {
  boxDimensions: PropTypes.shape({
    height: PropTypes.string.isRequired,
    width: PropTypes.string.isRequired,
  }),
  boxLink: PropTypes.shape({
    hardcodedURL: PropTypes.string,
    image: PropTypes.shape().isRequired,
    query: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  columnValue: PropTypes.number.isRequired,
};
